import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { verifyLicense } from "../lib/api";

const LicenseContext = createContext(null);
const STORAGE_KEY = "tk-license";

export function LicenseProvider({ children }) {
  const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const urlHasLicense = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("license");
  const [licenseKey, setLicenseKey] = useState(stored || null);
  const [unlocked, setUnlocked] = useState(!!stored);
  const [checking, setChecking] = useState(!!stored || urlHasLicense);

  // Re-verify a stored key against the backend on load (prevents tampering).
  useEffect(() => {
    if (!stored) { setChecking(false); return; }
    let active = true;
    verifyLicense(stored)
      .then((r) => {
        if (!active) return;
        if (r.valid) {
          setUnlocked(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setLicenseKey(null);
          setUnlocked(false);
        }
      })
      .catch(() => { /* offline: keep optimistic unlock */ })
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [stored]);

  // Re-lock immediately if the backend rejects the key on any protected call.
  useEffect(() => {
    const onInvalid = () => {
      localStorage.removeItem(STORAGE_KEY);
      setLicenseKey(null);
      setUnlocked(false);
    };
    window.addEventListener("tk-license-invalid", onInvalid);
    return () => window.removeEventListener("tk-license-invalid", onInvalid);
  }, []);

  const unlock = useCallback(async (key) => {
    const r = await verifyLicense(key);
    if (r.valid) {
      const norm = key.trim().toUpperCase();
      localStorage.setItem(STORAGE_KEY, norm);
      setLicenseKey(norm);
      setUnlocked(true);
    }
    return r;
  }, []);

  // Called right after a successful purchase (already verified server-side).
  const activate = useCallback((key) => {
    const norm = (key || "").trim().toUpperCase();
    if (!norm) return;
    localStorage.setItem(STORAGE_KEY, norm);
    setLicenseKey(norm);
    setUnlocked(true);
  }, []);

  // Owner/shared unlock via URL param (?license=KEY). Driven by <LicenseUrlWatcher/>
  // inside the Router so it fires on both first load AND client-side navigation.
  const resolveUrlLicense = useCallback(async (key) => {
    setChecking(true);
    try {
      return await unlock(key);
    } finally {
      setChecking(false);
    }
  }, [unlock]);

  return (
    <LicenseContext.Provider value={{ licenseKey, unlocked, checking, unlock, activate, resolveUrlLicense }}>
      {children}
    </LicenseContext.Provider>
  );
}

export const useLicense = () => useContext(LicenseContext);
